/**
 * ONE-TRUTH placement resolver for materialising a block INSERT's ATTRIB into a
 * standalone TEXT during EXPLODE.
 *
 * Both EXPLODE paths call THIS so the optimistic shell-preview mutation
 * (`packages/command-engine/src/blockMutations.ts` `explodeInsertMutation`, →
 * `EntityPreview`) and the reconciled persisted-document reducer
 * (`apps/web/src/app/state/blockReducerHelpers.ts`
 * `explodeBlockInsertInDocument`, → typed `DrawingEntity`) can NOT diverge
 * (shell↔doc parity — the SAME rationale that backs the sibling
 * `ellipseExplodeGeometry.ts`), and BOTH honour the world-vs-block-local
 * `overridePosition` invariant (#470, Lane F) instead of the pre-#470
 * block-local-only treatment that DOUBLE-TRANSFORMED an imported ATTRIB.
 *
 * # The world-vs-local invariant
 *
 * Sources: Autodesk DXF reference (ATTRIB is a full OCS entity stored with the
 * block content); ezdxf `add_auto_attribs` (`insert.matrix44() · attdef_pos`);
 * this repo's render truth (`scene3d/adapters/typed/attdefFromEntity.ts`
 * `resolvePlacement` + the `AttribOuterTransform` decomposition) and export
 * truth (`dxfBridge.attribExportPosition.ts resolveAttribExportGeometry`); the
 * model INVARIANT documented on `blockInsertAttributeSchema.overridePosition`.
 *
 *   - **`overridePosition` SET → WORLD/absolute placement.** The DXF group 10/20
 *     an ATTRIB carries is the placed point in the frame the ATTRIB is authored
 *     in — it ALREADY bakes in the ATTRIB's OWN immediate INSERT affine. So it
 *     MUST NOT run through that INSERT's `matrix` a SECOND time (that is the
 *     #470 double-transform defect — an imported ATTRIB at world (102,103) under
 *     an INSERT at (100,100) exploded near (202,203)). It rides ONLY the OUTER
 *     residual — the MINSERT per-cell grid offset (a placement of a REPETITION,
 *     not the INSERT's own affine), IDENTITY for a plain / base (row0/col0)
 *     cell. Its height/rotation/style are the ABSOLUTE DXF group values,
 *     byte-consistent with the export encoder + render (`overrideHeight ?? 2.5`,
 *     `overrideRotation ?? 0`, `overrideStyle ?? "Standard"`) — NOT re-scaled by
 *     |scaleX| or re-rotated by the INSERT rotation (the pure-translation grid
 *     residual carries no scale/rotation of its own).
 *   - **`overridePosition` UNSET → BLOCK-LOCAL template placement (unchanged
 *     pre-#470 behaviour).** A value-only DDATTE edit / freshly-authored INSERT
 *     defers to the ATTDEF template anchor in block-local space, which rides the
 *     FULL INSERT affine `matrix` exactly like every other block child —
 *     height·|scaleX|, rotation + the INSERT rotation. `overrideHeight/Rotation/
 *     Style`, when seeded from the template, resolve to the same value either
 *     way. This regime anchors on the ATTDEF's RAW `insertionPoint` and emits a
 *     left-justified TEXT with the width-factor/oblique defaults — NOT yet at
 *     full render parity for a JUSTIFIED ATTDEF (see "Known block-local
 *     divergences from render truth" below).
 *
 * # Known block-local divergences from render truth
 *   (pre-existing; tracked as the EXPLODE-fidelity follow-up, NOT this #470
 *   world-invariant slice — hence the world-vs-local scope of the parity claim)
 *
 * The WORLD regime reaches render/export parity for the fields it resolves
 * (position / height / rotation / style — what #470 fixed and the unit suite
 * locks). The BLOCK-LOCAL regime rides the INSERT affine like render but does
 * NOT yet reproduce render's `attdefFromEntity.ts resolvePlacement` in three
 * respects — the SAME gaps the PDF-plot twin (`insertConverter.ts
 * attributeToTextRun`) carries, so they close together:
 *   1. **Anchor.** This resolver takes the ATTDEF's raw `insertionPoint`
 *      (DXF 10/20); render takes `resolveTemplateAnchor(attdef)`, which returns
 *      the `alignmentPoint` (DXF 11/21) for a justified ATTDEF (aligned / fit /
 *      middle / centre / right / …). A justified template therefore explodes at
 *      the wrong anchor.
 *   2. **Justification / width-factor / oblique.** Both reducers emit the TEXT
 *      left-justified with widthFactor=1 / oblique=0; render carries the
 *      ATTDEF's own `hJustification`/`vJustification`/`widthFactor`/
 *      `obliqueAngle` onto the text.
 *   3. **Height scale axis.** This resolver scales the template height by
 *      |scaleX|; render scales by |scaleY| (`blockTransform.ts` `heightScale`).
 *      Identical under the common uniform-scale INSERT; diverges only under a
 *      non-uniform block scale.
 * Closing (1)–(3) means threading the ATTDEF's justification / width / oblique +
 * `scaleY` through BOTH this resolver and the two reducers' TEXT builders, plus
 * a justified-ATTDEF visual baseline — a Category-C change deliberately deferred
 * from this world-invariant fix.
 *
 * # The OUTER residual (`cellDelta`) — the WORLD regime's per-cell grid offset
 *
 * A WORLD `overridePosition` is placed for the base (row0/col0) cell; each
 * repeated MINSERT cell renders the label shifted by the block's own per-cell
 * grid offset. `cellDelta` is EXACTLY that offset, byte-identical to the live
 * render path (`scene3d/adapters/typed/blockFromEntity.ts` →
 * `transformBlockDirection({x: col·columnSpacing, y: row·rowSpacing}, transform)`
 * threaded as the `AttribOuterTransform`): the block's rotate+scale linear part
 * `R(rotationDeg)·S(scaleX, scaleY)` applied to the raw grid step — computed by
 * the shared `resolveMinsertCellOffset` below so both reducers stay byte-equal.
 * For a plain INSERT or the base cell it is `{0,0}` → a geometric no-op, so a
 * top-level imported ATTRIB explodes at its verbatim world point (the #470 fix).
 *
 * #482 — this offset RIDES the block's rotate+scale (render truth); the pre-#482
 * code passed the RAW unrotated/unscaled step (`origin − baseOrigin`), so a
 * rotated/scaled MINSERT exploded the WORLD label OFF its rendered spot (the
 * label rendered at `overridePosition + R·S·rawStep` in-block but exploded to
 * `overridePosition + rawStep`). The offset uses the CCW-positive
 * `+rotationDeg` sign — the DXF/AutoCAD convention (ezdxf `Insert.matrix44()`)
 * shared by the typed render (`transformBlockDirection`) AND, as of @codex
 * round-20, by `composeInsertMatrix` itself in BOTH explode paths: the matrix
 * previously carried `blockMatrix.ts`'s `-rotationDeg`, which is an
 * SVG-authoring artifact (the scene renderer composes it inside a parent
 * `scale(1,-1)` group) and had no business in model space. Cell origins, the
 * child-geometry affine, the WORLD-ATTRIB residual and every composed scalar
 * rotation now share ONE sign — a cell's geometry and its label cannot step
 * opposite ways again.
 */

import type {
  AttdefEntity,
  BlockInsertAttribute,
} from "@dxf-ai-editor/drawing-model";

import type { ExplodeAffineMatrix } from "./ellipseExplodeGeometry";

export interface ExplodedAttribPlacementInput {
  /** The per-INSERT ATTRIB being materialised into a standalone TEXT. */
  attrib: BlockInsertAttribute;
  /**
   * The ATTDEF template matched by tag inside the block definition, already
   * narrowed to the `Attdef` union member. `undefined` when the block carries
   * no matching ATTDEF (an orphaned ATTRIB — the ATTDEF was removed/renamed),
   * in which case the block-local fallback uses the AutoCAD hard defaults
   * (block-origin anchor / 2.5 / 0 / "Standard").
   */
  matchingAttdef: AttdefEntity | undefined;
  /**
   * THIS cell's FULL INSERT affine (T(origin)·R·S·T(-basePoint)). The
   * block-local regime rides it; the world regime never touches it.
   */
  matrix: ExplodeAffineMatrix;
  /**
   * OUTER residual for the WORLD regime — the block's rotate+scale grid offset
   * to THIS MINSERT cell (`{0,0}` for a plain INSERT or the base cell). Render
   * truth (`resolveMinsertCellOffset`): `R(rotationDeg)·S · (col·columnSpacing,
   * row·rowSpacing)`. Added verbatim to a WORLD `overridePosition`; never runs
   * through the INSERT `matrix`.
   */
  cellDelta: { x: number; y: number };
  /** INSERT rotation (degrees) — added to a block-local template rotation. */
  parentRotationDeg: number;
  /**
   * INSERT x-scale — scales a block-local template height (AutoCAD scales the
   * exploded attribute text by the reference scale). Absolute value applied.
   */
  scaleX: number;
}

/** The resolved standalone-TEXT placement (entity-shape-agnostic). */
export interface ExplodedAttribPlacement {
  insertionPoint: { x: number; y: number };
  height: number;
  rotation: number;
  styleName: string;
  /**
   * GitHub Codex review PR #558 round 2 P2 — DXF group 71 (generation
   * flags, the MIRRTEXT/EATTEDIT handedness bit). Byte-consistent with
   * the render truth (`attdefFromEntity.ts::attdefFromEntity`): a WORLD
   * placement reads `attrib.overrideGenerationFlags ?? 0` (the
   * per-instance EATTEDIT override, INCLUDING the bit MIRROR toggles);
   * a block-local placement reads the matched ATTDEF's own
   * `generationFlags` (an orphan ATTRIB with no matching ATTDEF has no
   * flags source, so 0). Without this, MIRROR→EXPLODE silently
   * un-mirrored a fully-reflected WORLD attribute even though its
   * position/rotation survived.
   */
  generationFlags: number;
}

/**
 * Resolve where / how a visible ATTRIB becomes a TEXT when its owning INSERT is
 * EXPLODEd, honouring the world-vs-block-local `overridePosition` invariant
 * (module header). Callers build their own entity shape around this result.
 */
export function resolveExplodedAttribPlacement(
  input: ExplodedAttribPlacementInput,
): ExplodedAttribPlacement {
  const { attrib, matchingAttdef, matrix, cellDelta, parentRotationDeg, scaleX } =
    input;

  if (attrib.overridePosition !== undefined) {
    // WORLD/absolute regime (module header). The group-10/20 point already
    // baked in this INSERT's own affine, so ride ONLY the pure-translation
    // OUTER residual (`cellDelta`) — never the INSERT `matrix` — and keep the
    // height/rotation/style as the ABSOLUTE DXF group values (byte-consistent
    // with the export encoder + scene render).
    return {
      insertionPoint: {
        x: attrib.overridePosition.x + cellDelta.x,
        y: attrib.overridePosition.y + cellDelta.y,
      },
      height: attrib.overrideHeight ?? 2.5,
      rotation: attrib.overrideRotation ?? 0,
      styleName: attrib.overrideStyle ?? "Standard",
      // GitHub Codex review PR #558 round 2 P2 — mirrors
      // `attdefFromEntity.ts`'s WORLD-placement generation-flags
      // resolution EXACTLY.
      generationFlags: attrib.overrideGenerationFlags ?? 0,
    };
  }

  // BLOCK-LOCAL template regime (module header) — no explicit world position,
  // so follow the ATTDEF template anchor in block-local space and ride the FULL
  // INSERT affine `matrix` exactly like every other block child.
  const localAnchor = {
    x: matchingAttdef ? matchingAttdef.insertionPoint.x : 0,
    y: matchingAttdef ? matchingAttdef.insertionPoint.y : 0,
  };
  const localHeight =
    attrib.overrideHeight ?? (matchingAttdef ? matchingAttdef.height : 2.5);
  const localRotation =
    attrib.overrideRotation ?? (matchingAttdef ? matchingAttdef.rotation : 0);
  const localStyle =
    attrib.overrideStyle ?? (matchingAttdef ? matchingAttdef.styleName : "Standard");

  // Apply the INSERT affine to the block-local anchor. Inlined (single use) —
  // this is the `ExplodeAffineMatrix` mapping documented on its declaration
  // (`worldX = a·x + c·y + e`, `worldY = b·x + d·y + f`), not a reusable helper.
  return {
    insertionPoint: {
      x: matrix.a * localAnchor.x + matrix.c * localAnchor.y + matrix.e,
      y: matrix.b * localAnchor.x + matrix.d * localAnchor.y + matrix.f,
    },
    height: localHeight * Math.abs(scaleX),
    rotation: localRotation + parentRotationDeg,
    styleName: localStyle,
    // GitHub Codex review PR #558 round 2 P2 (local read-only follow-up) —
    // mirrors BOTH render-truth branches EXACTLY, which use DIFFERENT rules
    // here (unlike height/rotation/style above, which both branches resolve
    // via `attr.overrideX ?? templateX`): a MATCHED ATTDEF's block-local
    // regime rides the TEMPLATE's own flags unconditionally
    // (`attdefFromEntity.ts`'s `generationFlags: placement.worldPlacement ?
    // ... : attdef.generationFlags` — no override consulted, per that
    // function's own doc: "out of MIRRTEXT's scope"), while an ORPHAN
    // ATTRIB (no matching ATTDEF, so no template to source from) goes
    // through the ENTIRELY SEPARATE `orphanAttributeFromEntity`, whose
    // `generationFlags: attr.overrideGenerationFlags ?? 0` is UNCONDITIONAL
    // on `worldPlacement` — so an orphan's per-instance EATTEDIT handedness
    // edit (no `overridePosition`, block-local rides the INSERT affine) must
    // still be read here, not defaulted to 0.
    generationFlags: matchingAttdef
      ? matchingAttdef.generationFlags
      : attrib.overrideGenerationFlags ?? 0,
  };
}

/** Inputs for the shared MINSERT per-cell world grid offset (`cellDelta`). */
export interface MinsertCellOffsetInput {
  /** Zero-based grid row (0 = base row). */
  row: number;
  /** Zero-based grid column (0 = base column). */
  col: number;
  /** DXF group 44 — column spacing (block-local units, pre-scale). */
  columnSpacing: number;
  /** DXF group 45 — row spacing (block-local units, pre-scale). */
  rowSpacing: number;
  /** INSERT rotation (degrees). The 3D-scene `+rotationDeg` sign (render truth). */
  rotationDeg: number;
  /** INSERT x-scale — raw/signed (a mirrored INSERT mirrors the grid step). */
  scaleX: number;
  /** INSERT y-scale — raw/signed. */
  scaleY: number;
}

/**
 * The render's legacy scale guard (`blockFromEntity.guardScale`,
 * `flattenBlockInsertEntity:1080-1084`): a degenerate (non-finite / ~zero)
 * scale component normalises to 1, NEVER 0. Render builds its `transform.scale`
 * with this guard BEFORE it ever reaches `transformBlockDirection` for the
 * MINSERT per-cell offset (`blockFromEntity.ts:262-264` → the `flattenOneCell`
 * grid loop consumes the ALREADY-guarded `transform`, never the raw
 * `insert.scaleX/scaleY`) — so a schema-valid `scaleX: 0` MINSERT still steps
 * the grid by `columnSpacing·1`, one full spacing per cell, exactly like every
 * other (non-degenerate) scale. `resolveMinsertCellOffset` must apply the
 * SAME guard to its own raw `scaleX`/`scaleY` inputs (@codex "Use guarded
 * scales for MINSERT offsets") — every caller here passes the UNGUARDED
 * `insert.scaleX/scaleY` straight off the schema, so without this guard a
 * degenerate-scale MINSERT would collapse every non-base cell's WORLD ATTRIB
 * (and, via the shared `cellInsertPoint` callers, its child geometry) onto the
 * base cell instead of landing one spacing away where render puts it.
 *
 * ONE-TRUTH within this package: `nestedInsertExplode.ts` (its own,
 * materially different call site — nested-INSERT scale COMPOSITION) imports
 * THIS copy instead of keeping its own local one. `blockFromEntity.ts` (in
 * apps/web) keeps a SEPARATE local copy — packages cannot import apps/web —
 * which this doc comment mirrors intentionally, not a divergence.
 */
export function guardScale(value: number): number {
  return Number.isFinite(value) && Math.abs(value) > 1e-9 ? value : 1;
}

/**
 * The WORLD-regime `cellDelta` (module header "OUTER residual") for ONE MINSERT
 * cell: the block's rotate+scale applied to the raw grid step
 * `(col·columnSpacing, row·rowSpacing)`, byte-identical to the LIVE render path's
 * per-cell offset (`scene3d/adapters/typed/blockFromEntity.ts` →
 * `transformBlockDirection({x: col·columnSpacing, y: row·rowSpacing}, transform)`
 * — the linear part `R(rotationDeg)·S(scaleX, scaleY)`, NO translation /
 * basePoint subtract). A WORLD `overridePosition` renders at
 * `overridePosition + offset` inside its repeated cell, so the exploded
 * standalone TEXT must carry the SAME offset to land where it renders (the #482
 * fix — the pre-#482 code used the RAW unrotated/unscaled step, so a
 * rotated/scaled MINSERT exploded the label off its rendered spot).
 *
 * Scale-THEN-rotate order + the CCW-positive `+rotationDeg` sign match render's
 * `transformBlockDirection` exactly — and, as of @codex round-20, the explode
 * paths' `composeInsertMatrix` shares the same sign (its former `-rotationDeg`
 * was an SVG-authoring artifact — see its doc); `{0,0}` for the base (row0/col0) cell or a plain
 * INSERT (a geometric no-op → verbatim world point). ONE-TRUTH: both EXPLODE
 * paths (`blockMutations.ts` shell preview + `blockReducerHelpers.ts` persisted
 * reducer) call THIS so their WORLD `cellDelta` can NOT diverge (shell↔doc
 * parity — the same rationale that backs `resolveExplodedAttribPlacement`).
 *
 * `scaleX`/`scaleY` run through `guardScale` first (above) — render's
 * `transform.scale` is ALREADY guarded by the time it reaches
 * `transformBlockDirection`, so a degenerate raw `0` must normalise to `1`
 * here too, not collapse the grid step to zero.
 */
export function resolveMinsertCellOffset(
  input: MinsertCellOffsetInput,
): { x: number; y: number } {
  const rad = (input.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Scale THEN rotate — the exact order of render's `transformBlockDirection`
  // (`sx = v.x · scale.x` before the R multiply); guarded (never-zero) scale
  // so a mirrored INSERT mirrors the grid step identically to render, and a
  // degenerate zero scale steps a full spacing (guardScale → 1) instead of
  // collapsing every cell onto the base cell.
  const vx = input.col * input.columnSpacing * guardScale(input.scaleX);
  const vy = input.row * input.rowSpacing * guardScale(input.scaleY);
  return {
    x: vx * cos - vy * sin,
    y: vx * sin + vy * cos,
  };
}
